import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Lock, Gift } from 'lucide-react';
import styles from './form.module.css';

// The outcome is always decided server-side (see responseController.submitResponse) so it
// can't be gamed from the client, and is locked in after one spin — same one-shot rule as
// Scratch Card, no "update response" flow here.
//
// Interaction: three reels spin together, then stop left-to-right and settle on the same
// symbol — a classic slot "triple match" reveal for whichever outcome the server drew. Each
// configured outcome maps to a fixed symbol from a small palette (by its position in the
// options list), purely so the reels have something concrete to land on; the actual prize is
// always the outcome's text, shown below the reels once they stop.

const SYMBOL_PALETTE = ['🍒', '🍋', '🍊', '⭐', '7️⃣', '🍇', '🔔', '💎', '🍀', '🍉'];
const PLACEHOLDER_SYMBOL = '❔';
const MIN_SPIN_MS = 900; // reels must visibly spin at least this long before any reel can land
const REEL_STOP_STAGGER_MS = 350; // gap between each reel locking in, left to right
const REVEAL_BUFFER_MS = 300; // pause after the last reel lands before showing the prize
const CYCLE_INTERVAL_MS = 80; // how often an unlanded reel's displayed symbol changes while spinning
const POPPER_PARTICLES_PER_SIDE = 18;
const POPPER_COLORS = ['#C5F135', 'var(--cat-amber)', 'var(--cat-pink)', 'var(--cat-teal)', 'var(--cat-violet)', 'var(--cat-blue)'];
const POPPER_VISIBLE_MS = 1900;

function randomSymbol() {
    return SYMBOL_PALETTE[Math.floor(Math.random() * SYMBOL_PALETTE.length)];
}

// Deterministic by the outcome's position in the configured options list, so every
// participant who wins the same prize sees the same reel symbol for it.
function symbolForOutcome(outcomeValue, options) {
    if (outcomeValue == null) return SYMBOL_PALETTE[0];
    const idx = options.indexOf(outcomeValue);
    if (idx < 0) return SYMBOL_PALETTE[0];
    return SYMBOL_PALETTE[idx % SYMBOL_PALETTE.length];
}

const SlotMachineForm = ({ content, onSubmit, isPreview = false, isLocked = false, userAnswer = null }) => {
    const outcome = userAnswer?.outcome ?? null;
    const options = Array.isArray(content?.options) ? content.options.map(o => String(o || '').trim()).filter(Boolean) : [];
    const hasOutcomes = options.length > 0;
    const targetSymbol = symbolForOutcome(outcome, options);

    // 'idle' (not spun yet) -> 'spinning' (reels cycling while the server draw is in flight)
    // -> 'revealed'. Starting in 'revealed' if this participant already has an outcome (e.g.
    // after a page reload) skips the spin/landing sequence and confetti entirely.
    const [phase, setPhase] = useState(outcome != null ? 'revealed' : 'idle');
    const [reelLanded, setReelLanded] = useState([outcome != null, outcome != null, outcome != null]);
    const [spinTick, setSpinTick] = useState(0);
    const [confettiKey, setConfettiKey] = useState(0);
    const [showPoppers, setShowPoppers] = useState(false);

    const animationDoneRef = useRef(phase === 'revealed');
    const outcomeArrivedRef = useRef(outcome != null);
    const spinIntervalRef = useRef(null);
    const landingTimersRef = useRef([]);

    const interactionDisabled = isPreview || isLocked || !hasOutcomes || phase !== 'idle';

    // Stops the spin interval and, left to right, locks each reel onto the target symbol —
    // only once both the minimum spin time has elapsed AND the server's outcome has arrived
    // (whichever finishes second is what actually starts the landing sequence).
    const tryLand = useCallback(() => {
        if (!(animationDoneRef.current && outcomeArrivedRef.current)) return;
        if (spinIntervalRef.current) { clearInterval(spinIntervalRef.current); spinIntervalRef.current = null; }
        [0, 1, 2].forEach((i) => {
            const t = setTimeout(() => {
                setReelLanded(prev => prev.map((v, idx) => (idx === i ? true : v)));
                if (i === 2) {
                    const t2 = setTimeout(() => {
                        setPhase('revealed');
                        setConfettiKey(k => k + 1);
                    }, REVEAL_BUFFER_MS);
                    landingTimersRef.current.push(t2);
                }
            }, i * REEL_STOP_STAGGER_MS);
            landingTimersRef.current.push(t);
        });
    }, []);

    // The server may resolve before or after the minimum spin time — either order must lead
    // to landing once both are ready. Also handles a restored answer: JoinPage restores
    // userAnswer asynchronously, so it can arrive after this form already mounted in 'idle' —
    // jump straight to 'revealed' with no spin/confetti replay in that case.
    useEffect(() => {
        if (outcome == null) return;
        outcomeArrivedRef.current = true;
        if (phase === 'idle') {
            animationDoneRef.current = true;
            setReelLanded([true, true, true]);
            setPhase('revealed');
        } else {
            tryLand();
        }
    }, [outcome, phase, tryLand]);

    // Drives the spinning visual and the minimum-spin timer once a spin starts.
    useEffect(() => {
        if (phase !== 'spinning') return;
        spinIntervalRef.current = setInterval(() => setSpinTick(t => t + 1), CYCLE_INTERVAL_MS);
        const minSpinTimer = setTimeout(() => {
            animationDoneRef.current = true;
            tryLand();
        }, MIN_SPIN_MS);
        return () => {
            clearTimeout(minSpinTimer);
            if (spinIntervalRef.current) { clearInterval(spinIntervalRef.current); spinIntervalRef.current = null; }
        };
    }, [phase, tryLand]);

    useEffect(() => () => {
        landingTimersRef.current.forEach(clearTimeout);
        if (spinIntervalRef.current) clearInterval(spinIntervalRef.current);
    }, []);

    // Fires only on a fresh reveal (confettiKey increments) — not when this participant
    // already had an outcome on mount (e.g. after a page reload).
    useEffect(() => {
        if (confettiKey === 0) return;
        setShowPoppers(true);
        const timer = setTimeout(() => setShowPoppers(false), POPPER_VISIBLE_MS);
        return () => clearTimeout(timer);
    }, [confettiKey]);

    const handleSpin = () => {
        if (interactionDisabled || phase !== 'idle') return;
        try { navigator.vibrate?.(15); } catch (_) { /* vibration unsupported/blocked — purely cosmetic */ }
        animationDoneRef.current = false;
        outcomeArrivedRef.current = outcome != null;
        setReelLanded([false, false, false]);
        setPhase('spinning');
        onSubmit({});
    };

    const reelSymbol = (i) => {
        if (phase === 'revealed' || reelLanded[i]) return targetSymbol;
        if (phase === 'spinning') return randomSymbol();
        return PLACEHOLDER_SYMBOL;
    };

    return (
        <div className={styles.form}>
            <div className={styles.question}>
                {content.question || 'Spin the reels for a chance to win!'}
                {isLocked && outcome == null && <span className={styles.lockBadge}><Lock size={12} aria-hidden="true" /> Voting Locked</span>}
            </div>
            {content.settings?.description && content.settings?.descText && (
                <div className={styles.description}>{content.settings.descText}</div>
            )}

            {!hasOutcomes ? (
                <div className={styles.slotMachine} style={{ margin: 'var(--space-4) 0 var(--space-2)' }}>
                    <span className={styles.slotNotSetUp}>Not set up yet</span>
                </div>
            ) : (
                <div className={styles.slotMachine}>
                    <div className={styles.slotReelsRow} aria-hidden="true">
                        {[0, 1, 2].map((i) => (
                            <div
                                key={i}
                                className={[
                                    styles.slotReel,
                                    phase === 'spinning' && !reelLanded[i] ? styles.slotReelSpinning : '',
                                    reelLanded[i] && phase !== 'idle' ? styles.slotReelLanded : '',
                                ].filter(Boolean).join(' ')}
                            >
                                <span key={reelSymbol(i) + spinTick} className={styles.slotReelSymbol}>{reelSymbol(i)}</span>
                            </div>
                        ))}
                    </div>

                    {phase === 'revealed' ? (
                        <div className={styles.slotOutcomeReveal}>
                            <Gift size={20} aria-hidden />
                            <span>{outcome}</span>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className={styles.slotSpinBtn}
                            onClick={handleSpin}
                            disabled={interactionDisabled}
                        >
                            {phase === 'spinning' ? 'Spinning…' : 'SPIN'}
                        </button>
                    )}
                </div>
            )}

            {phase === 'spinning' && <p className={styles.submittedHint}>Spinning the reels…</p>}
            {phase === 'revealed' && <p className={styles.submittedHint}>You've already spun — one try per participant.</p>}

            <PopperCelebration show={showPoppers} />
        </div>
    );
};

// Two party-popper bursts from the bottom corners — same celebration used by Scratch Card,
// portaled to <body> so it isn't clipped by any scroll container.
function PopperCelebration({ show }) {
    if (!show || typeof document === 'undefined') return null;
    return createPortal(
        <div className={styles.popperOverlay} aria-hidden="true">
            {['left', 'right'].map((side) => (
                <div key={side} className={side === 'left' ? styles.popperOriginLeft : styles.popperOriginRight}>
                    {Array.from({ length: POPPER_PARTICLES_PER_SIDE }).map((_, i) => {
                        const baseAngle = side === 'left' ? -60 : -120;
                        const spread = 55;
                        const angle = baseAngle + (Math.random() * spread - spread / 2);
                        const distance = 260 + Math.random() * 260;
                        const size = 6 + Math.round(Math.random() * 6);
                        const isCircle = i % 2 === 0;
                        return (
                            <span
                                key={i}
                                className={styles.popperPiece}
                                style={{
                                    '--angle': `${angle}deg`,
                                    '--distance': `${distance}px`,
                                    '--delay': `${Math.random() * 180}ms`,
                                    '--duration': `${1200 + Math.random() * 500}ms`,
                                    '--spin': `${(Math.random() > 0.5 ? 1 : -1) * (360 + Math.random() * 360)}deg`,
                                    width: `${size}px`,
                                    height: `${isCircle ? size : size * 1.8}px`,
                                    borderRadius: isCircle ? '50%' : '1px',
                                    background: POPPER_COLORS[i % POPPER_COLORS.length],
                                }}
                            />
                        );
                    })}
                </div>
            ))}
        </div>,
        document.body
    );
}

export default SlotMachineForm;
